import React from 'react';
import {
  X,
  MessageCircle,
  Phone,
  Mail,
  Briefcase,
} from 'lucide-react';
import { Button } from '../ui/button';
import ChatModalPortal from './ChatModalPortal';

export const UserProfileDialog = ({
  open,
  onClose,
  userProfile,
  isOnline = false,
  onStartChat,
  onStartCall,
}) => {
  if (!open || !userProfile) return null;

  const name = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(' ')
    || 'Student';
  const initials = name
    .trim()
    .split(/\s+/)
    .map((p) => p[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <ChatModalPortal>
      <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-3xl border-0 bg-white shadow-2xl flex flex-col">
        <div aria-hidden="true" className="flex h-1.5 w-full">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <div className="relative px-6 pb-8 pt-6 text-center text-zinc-950">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition"
            aria-label="Mbyll dritaren"
            title="Mbyll"
          >
            <X size={18} />
          </button>

          <div className="relative mx-auto mb-3 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-zinc-950 text-xl font-black text-[#FFCE00] shadow-lg ring-4 ring-amber-100">
            {userProfile.avatar_url ? <img src={userProfile.avatar_url} alt="" className="h-full w-full object-cover" /> : initials}
            <span className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${isOnline ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
          </div>

          <h3 className="text-lg font-bold tracking-tight text-zinc-950">{name}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {userProfile.profession || 'Student i Gjermanishtes'}
          </p>

          <div className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${isOnline ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-zinc-200 bg-zinc-50 text-zinc-500'}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
            {isOnline ? 'Online tani' : 'Jashtë linje'}
          </div>
        </div>

        {/* Quick action buttons row */}
        <div className="-mt-5 flex justify-center gap-3 px-6 z-10">
          <Button
            size="sm"
            onClick={() => {
              if (onStartChat) onStartChat(userProfile);
              onClose();
            }}
            className="rounded-xl bg-zinc-950 text-[#FFCE00] hover:bg-[#DD0000] hover:text-white font-bold shadow-md gap-1.5 h-10 px-4"
          >
            <MessageCircle size={15} />
            Shkruaj Mesazh
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (onStartCall) onStartCall(userProfile);
              onClose();
            }}
            className="rounded-xl border-zinc-200 bg-white text-zinc-800 hover:bg-zinc-50 shadow-md gap-1.5 h-10 px-4"
          >
            <Phone size={15} className="text-emerald-600" />
            Telefono
          </Button>
        </div>

        {/* Details section */}
        <div className="p-6 space-y-4 pt-5">
          <div className="space-y-2.5">
            <div className="flex items-center gap-3 text-xs text-zinc-700">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 shrink-0">
                <Briefcase size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[10px] text-zinc-400 uppercase font-bold block">Profesioni</span>
                <span className="font-semibold text-zinc-900 truncate block">
                  {userProfile.profession || 'Përdorues i platformës'}
                </span>
              </div>
            </div>

            {userProfile.email && (
              <div className="flex items-center gap-3 text-xs text-zinc-700">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 shrink-0">
                  <Mail size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[10px] text-zinc-400 uppercase font-bold block">Email</span>
                  <span className="font-semibold text-zinc-900 truncate block">
                    {userProfile.email}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </ChatModalPortal>
  );
};

export default UserProfileDialog;
